/**
 * Corporate Workspace — Climate Portfolio & Project Conversion Engine
 * Route: /corporate/workspace
 *
 * Flow: Measure → Aggregate → Identify → Decide → Structure → Submit
 *
 * 10 steps:
 *  1. Organization Setup
 *  2. Data Aggregation (Scope 1/2/3 across sites)
 *  3. Impact & Hotspot Analysis
 *  4. Action Portfolio
 *  5. Carbon Value & Eligibility
 *  6. Decision Engine (Structure vs Recommend)
 *  7. Project Structuring / Project Recommendations
 *  8. Article 6 & NDC Alignment
 *  9. Documentation Preparation
 * 10. Submit to FI / Market
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, InsightCard, CTAButton } from "@/components/ds";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
  Building2, BarChart2, Activity, Layers, Zap, GitBranch, ListChecks,
  Globe, FileText, DollarSign, ArrowRight, CheckCircle2, RefreshCw,
  AlertCircle, ChevronRight, TrendingDown, Target, Plus, Info,
  Check, Shield, BookOpen, Award
} from "lucide-react";

const STEPS = [
  { id: 1,  key: "setup",           label: "Organization Setup",           icon: Building2    },
  { id: 2,  key: "data",            label: "Data Aggregation",             icon: BarChart2    },
  { id: 3,  key: "hotspots",        label: "Impact & Hotspots",            icon: Activity     },
  { id: 4,  key: "actions",         label: "Action Portfolio",             icon: Layers       },
  { id: 5,  key: "carbon_value",    label: "Carbon Value & Eligibility",   icon: Zap          },
  { id: 6,  key: "decision",        label: "Decision Engine",              icon: GitBranch    },
  { id: 7,  key: "structuring",     label: "Project Structuring",          icon: ListChecks   },
  { id: 8,  key: "ndc",             label: "Article 6 & NDC Alignment",   icon: Globe        },
  { id: 9,  key: "documentation",   label: "Documentation",               icon: FileText     },
  { id: 10, key: "submission",      label: "Submit to FI / Market",       icon: DollarSign   },
];

// ── Step Rail ─────────────────────────────────────────────────────────────
function StepRail({ activeStep, completedKeys, onStep }) {
  return (
    <div className="w-[220px] flex-shrink-0 bg-[#0B1220] border-r border-white/8 h-full overflow-y-auto sticky top-14"
      data-testid="corp-step-rail">
      <div className="p-4">
        <p className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-3 px-2">Conversion Steps</p>
        {STEPS.map(s => {
          const done   = completedKeys.includes(s.key);
          const active = activeStep === s.key;
          const Icon   = s.icon;
          return (
            <button key={s.key} onClick={() => onStep(s.key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium text-left transition-all mb-0.5 ${
                active ? "bg-teal-600/20 text-teal-300 border border-teal-600/30" :
                done   ? "text-emerald-400 hover:bg-white/5" :
                         "text-slate-500 hover:bg-white/5 hover:text-slate-300"
              }`}
              data-testid={`corp-step-nav-${s.key}`}>
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                active ? "bg-teal-600/30" : done ? "bg-emerald-900/30" : "bg-white/5"
              }`}>
                {done
                  ? <Check size={10} className="text-emerald-400" strokeWidth={3} />
                  : <Icon size={10} className={active ? "text-teal-300" : "text-slate-500"} />
                }
              </div>
              <span className="truncate">{s.id}. {s.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Right Panel — Executive View ──────────────────────────────────────────
function ExecutivePanel({ summary, recommendations, readiness }) {
  if (!summary) return null;

  const hasData     = summary.total_reduction_tco2e > 0;
  const vcmLow      = summary.vcm_value_low;
  const vcmHigh     = summary.vcm_value_high;
  const isStructure = summary.recommendation === "project_structuring";

  return (
    <div className="w-[260px] flex-shrink-0 bg-[#0B1220] border-l border-white/8 h-full overflow-y-auto sticky top-14 hidden xl:block"
      data-testid="corp-exec-panel">
      <div className="p-4 space-y-4">
        <p className="text-[9px] text-slate-400 uppercase tracking-widest font-bold">Executive View</p>

        {/* Portfolio status */}
        <div className={`rounded-lg p-3 border ${
          isStructure ? "bg-emerald-900/20 border-emerald-600/30" : "bg-amber-900/20 border-amber-600/30"
        }`}>
          <p className={`text-xs font-bold ${isStructure ? "text-emerald-400" : "text-amber-400"}`}>
            {isStructure ? "Portfolio Ready for Structuring" : "Actions Needed"}
          </p>
          <p className="text-[9px] text-slate-400 mt-0.5">
            {isStructure ? "Convert initiatives into structured projects" : "Add or strengthen decarbonization actions"}
          </p>
        </div>

        {/* Portfolio metrics */}
        <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
          <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-2">Portfolio</p>
          {[
            ["Initiatives",   summary.total_initiatives],
            ["Total Reduction",hasData ? `${summary.total_reduction_tco2e}t/yr` : "—"],
            ["Total CAPEX",   summary.total_capex_usd > 0 ? `$${(summary.total_capex_usd/1000).toFixed(0)}k` : "—"],
          ].map(([l, v]) => (
            <div key={l} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
              <span className="text-[9px] text-slate-400">{l}</span>
              <span className="text-xs font-bold text-white">{v}</span>
            </div>
          ))}
        </div>

        {/* Carbon value */}
        {vcmLow > 0 && (
          <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-2">Carbon Value (indicative)</p>
            <p className="text-base font-bold text-teal-400">${(vcmLow/1000).toFixed(0)}k–${(vcmHigh/1000).toFixed(0)}k/yr</p>
            <p className="text-[9px] text-slate-500 mt-0.5">at $15–30/tCO₂e</p>
          </div>
        )}

        {/* Next action */}
        <div className="bg-teal-900/20 border border-teal-600/30 rounded-lg p-3">
          <p className="text-[9px] text-teal-400 font-bold uppercase tracking-wider mb-1">Next Action</p>
          <p className="text-[10px] text-slate-300">
            {!hasData ? "Add emissions data and initiatives to start" :
             isStructure ? "Convert your strongest initiative to a Project Assessment" :
             "Review recommendations and add qualifying initiatives"}
          </p>
        </div>

        {/* Portfolio Readiness Index */}
        {readiness && (
          <div className="bg-[#0F172A] rounded-lg p-3 border border-white/5" data-testid="portfolio-readiness-panel">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-2">Portfolio Readiness</p>
            <div className={`text-xs font-bold mb-2 px-2 py-1 rounded-lg inline-block ${
              readiness.overall === "fi_ready"          ? "bg-emerald-600/20 text-emerald-400" :
              readiness.overall === "structuring_ready" ? "bg-teal-600/20 text-teal-400"      :
              readiness.overall === "developing"        ? "bg-amber-600/20 text-amber-400"     :
                                                          "bg-slate-700 text-slate-400"
            }`}>{readiness.overall_label}</div>
            <div className="space-y-2">
              {Object.values(readiness.dimensions).map(d => (
                <div key={d.label}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] text-slate-400">{d.label}</span>
                    <span className={`text-[9px] font-bold capitalize px-1.5 py-0.5 rounded-full ${
                      d.score === "strong"   ? "bg-emerald-900/30 text-emerald-400" :
                      d.score === "moderate" ? "bg-amber-900/30 text-amber-400"    :
                                               "bg-red-900/20 text-red-400"
                    }`}>{d.score}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${
                      d.score === "strong" ? "bg-emerald-500 w-full" :
                      d.score === "moderate" ? "bg-amber-500 w-2/3" : "bg-red-500 w-1/3"
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SECTION CARD ──────────────────────────────────────────────────────────
function StepCard({ number, icon: Icon, title, desc, children, footer }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5"
      data-testid={`corp-section-${number}`}>
      <div className="px-6 py-4 border-b border-slate-100 flex items-start gap-4 bg-slate-50/50">
        <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon size={16} className="text-teal-600" />
        </div>
        <div>
          <p className="text-[10px] text-teal-500 font-bold uppercase tracking-widest">Step {number}</p>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {desc && <p className="text-xs text-slate-400 mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
      {footer && <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center gap-3">{footer}</div>}
    </div>
  );
}

// ── STEP 1: Organization Setup (enterprise-level) ─────────────────────────
function Step1_Setup({ org, siteCount, summary }) {
  // Fallback: build org from summary data when auth context org is loading
  const displayOrg = org || (summary ? {
    name: summary.org_name,
    sector: summary.industry,
    country: summary.country,
  } : null);
  if (!displayOrg) return <div className="text-center text-xs text-slate-400 py-6">Loading organization...</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        {[
          ["Organization",     displayOrg.name],
          ["Industry",         displayOrg.sector || "—"],
          ["Primary Country",  displayOrg.country || "—"],
          ["Employees",        displayOrg.employee_count ? `${displayOrg.employee_count.toLocaleString()} people` : "—"],
          ["Sites / Plants",   siteCount > 0 ? `${siteCount} site${siteCount !== 1 ? "s" : ""} registered` : "Not configured"],
          ["HQ Country",       displayOrg.country || "—"],
        ].map(([l, v]) => (
          <div key={l} className="bg-slate-50 rounded-xl px-4 py-3">
            <p className="text-[10px] text-slate-400 font-semibold uppercase">{l}</p>
            <p className="text-sm font-semibold text-slate-900 mt-0.5">{v}</p>
          </div>
        ))}
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2">
        <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
        <p className="text-xs text-emerald-700">Organization confirmed. Industry benchmark context and NDC alignment are calibrated to your profile.</p>
      </div>
    </div>
  );
}

// ── STEP 2: Data Aggregation ───────────────────────────────────────────────
function Step2_Data({ orgId, navigate, summary }) {
  const hasData = (summary?.total_reduction_tco2e || 0) > 0;
  const n       = summary?.total_initiatives || 0;
  const capex   = summary?.total_capex_usd   || 0;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Capture emissions across all sites and business lines — Scope 1 (direct), Scope 2 (energy), Scope 3 (value chain).
        For large organizations, add one assessment per major site or production unit.
      </p>

      {/* Portfolio coverage summary when data exists */}
      {hasData && (
        <div className="bg-[#0F172A] rounded-xl p-5 text-white">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-3">Portfolio Coverage (from Action Portfolio)</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xl font-bold text-teal-400">{summary.total_reduction_tco2e}t</p>
              <p className="text-[10px] text-slate-400">CO₂e/yr addressable</p>
            </div>
            <div>
              <p className="text-xl font-bold text-white">{n}</p>
              <p className="text-[10px] text-slate-400">initiatives covering emissions</p>
            </div>
            <div>
              <p className="text-xl font-bold text-emerald-400">${(capex/1000000).toFixed(1)}M</p>
              <p className="text-[10px] text-slate-400">total CAPEX defined</p>
            </div>
          </div>
        </div>
      )}

      {/* Scope data entry */}
      <div className="space-y-2">
        {[
          { scope: "1", label: "Direct emissions (Scope 1)",    desc: "Fuel combustion, process emissions, fleet, generators", color: "text-orange-600 bg-orange-50" },
          { scope: "2", label: "Energy emissions (Scope 2)",    desc: "Grid electricity and purchased heat/steam",    color: "text-blue-600 bg-blue-50"   },
          { scope: "3", label: "Value chain (Scope 3)",         desc: "Logistics, raw materials, waste, sold products",  color: "text-purple-600 bg-purple-50" },
        ].map(s => (
          <div key={s.scope} className="flex items-start gap-3 bg-slate-50 rounded-xl px-4 py-3">
            <span className={`text-[9px] font-bold px-2 py-1 rounded-full flex-shrink-0 mt-0.5 ${s.color}`}>S{s.scope}</span>
            <div className="flex-1">
              <p className="text-xs font-semibold text-[#0F172A]">{s.label}</p>
              <p className="text-[10px] text-slate-400">{s.desc}</p>
            </div>
            <button onClick={() => navigate(`/assessments?org_id=${orgId}`)}
              className="flex-shrink-0 text-teal-600 hover:text-teal-700 text-xs font-semibold flex items-center gap-1">
              Add data <ChevronRight size={10} />
            </button>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
        <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-amber-700">
          For large organizations: add one assessment per major production site. Group smaller depots or logistics hubs into regional assessments.
          Aggregated totals feed the Portfolio Readiness Index and carbon value calculation.
        </p>
      </div>
    </div>
  );
}

// ── STEP 3: Impact & Hotspots ─────────────────────────────────────────────
function Step3_Hotspots({ initiatives, summary }) {
  if (!initiatives.length) {
    return (
      <div className="text-center py-6">
        <Activity size={24} className="text-slate-200 mx-auto mb-2" />
        <p className="text-xs text-slate-400">Add initiatives in Step 4 to see hotspot analysis</p>
      </div>
    );
  }

  const sorted = [...initiatives]
    .filter(i => i.expected_reduction_tco2e > 0)
    .sort((a, b) => b.expected_reduction_tco2e - a.expected_reduction_tco2e)
    .slice(0, 5);

  const total = sorted.reduce((s, i) => s + (i.expected_reduction_tco2e || 0), 0);

  // Category-based summary for enterprise view
  const byCat = initiatives.reduce((acc, i) => {
    const cat = CAT_LABELS[i.category] || "Other";
    acc[cat] = (acc[cat] || 0) + (i.expected_reduction_tco2e || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Your highest-impact decarbonization opportunities, ranked by emission reduction potential.
      </p>

      {/* Top initiatives */}
      <div>
        <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-2">Top Opportunities by Reduction Potential</p>
        <div className="space-y-2">
          {sorted.map((i, idx) => {
            const pct = total > 0 ? Math.round(i.expected_reduction_tco2e / total * 100) : 0;
            return (
              <div key={i.id} data-testid={`hotspot-${idx}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600 font-medium truncate mr-4">{i.name}</span>
                  <span className="font-bold text-[#0F172A] flex-shrink-0">{i.expected_reduction_tco2e}t · {pct}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-teal-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category coverage */}
      {Object.keys(byCat).length > 1 && (
        <SectionCard>

          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-2">Reduction by Technology Category</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byCat).sort((a,b)=>b[1]-a[1]).map(([cat, red]) => (
              <span key={cat} className="text-[10px] font-semibold px-2.5 py-1 bg-white border border-slate-200 rounded-full text-slate-600">
                {cat}: <strong className="text-teal-600">{Math.round(red)}t</strong>
              </span>
            ))}
          </div>
        
        </SectionCard>
      )}

      {sorted[0] && (
        <div className="bg-[#0F172A] rounded-xl px-4 py-3">
          <p className="text-[10px] text-teal-400 font-semibold uppercase tracking-wide mb-0.5">Key Insight</p>
          <p className="text-xs text-white">
            {sorted[0].name} is your largest single opportunity at {sorted[0].expected_reduction_tco2e}t CO₂e/yr
            — accounting for {total > 0 ? Math.round(sorted[0].expected_reduction_tco2e/total*100) : 0}% of total portfolio reduction potential.
          </p>
        </div>
      )}
    </div>
  );
}

// ── STEP 4: Action Portfolio ───────────────────────────────────────────────
const CAT_LABELS = {
  renewable_energy: "Renewable Energy", energy_efficiency: "Energy Efficiency",
  fuel_switch: "Fuel Switch", waste_reduction: "Waste Reduction",
  fleet_electrification: "Fleet Electrification", process_optimisation: "Process Optimisation",
  materials_substitution: "Materials", low_carbon_materials: "Low-Carbon Materials", other: "Other",
};
const STATUS_COLORS = {
  planned: "bg-slate-100 text-slate-600", in_progress: "bg-blue-100 text-blue-700", completed: "bg-emerald-100 text-emerald-700",
};

function Step4_Actions({ initiatives, orgId, navigate }) {
  const total_r = initiatives.reduce((s, i) => s + (i.expected_reduction_tco2e || 0), 0);
  const total_c = initiatives.reduce((s, i) => s + (i.capex_usd || 0), 0);
  const total_s = initiatives.reduce((s, i) => s + (i.estimated_savings_usd || 0), 0);

  return (
    <div className="space-y-4">
      {/* Portfolio totals */}
      {initiatives.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            ["Total Reduction",    `${Math.round(total_r)}t/yr`,      "text-teal-600"  ],
            ["Total CAPEX",        `$${(total_c/1000).toFixed(0)}k`,  "text-[#0F172A]" ],
            ["Annual Savings",     `$${Math.round(total_s).toLocaleString()}/yr`, "text-emerald-600"],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
              <p className={`text-lg font-bold font-mono ${c}`}>{v}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{l}</p>
            </div>
          ))}
        </div>
      )}

      {initiatives.length === 0 ? (
        <div className="text-center py-6">
          <Layers size={24} className="text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400 mb-3">No initiatives yet — add your decarbonization actions</p>
          <button onClick={() => navigate("/corporate/initiatives")}
            className="flex items-center gap-2 mx-auto px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
            <Plus size={13} /> Add Initiatives
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {initiatives.map(i => (
            <div key={i.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-xs font-bold text-[#0F172A] truncate">{i.name}</p>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${STATUS_COLORS[i.status] || "bg-slate-100 text-slate-500"}`}>
                    {i.status?.replace(/_/g," ")}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400">{CAT_LABELS[i.category] || i.category}</p>
              </div>
              <div className="text-right flex-shrink-0">
                {i.expected_reduction_tco2e > 0 && <p className="text-xs font-bold text-teal-600">{i.expected_reduction_tco2e}t/yr</p>}
                {i.capex_usd > 0 && <p className="text-[10px] text-slate-400">${(i.capex_usd/1000).toFixed(0)}k</p>}
              </div>
            </div>
          ))}
          <button onClick={() => navigate("/corporate/initiatives")}
            className="text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1 mt-1">
            <Plus size={12} /> Add more initiatives
          </button>
        </div>
      )}
    </div>
  );
}

// ── STEP 5: Carbon Value & Eligibility ────────────────────────────────────
function Step5_CarbonValue({ summary }) {
  const reduction = summary?.total_reduction_tco2e || 0;
  const vcmLow = summary?.vcm_value_low || 0;
  const vcmHigh = summary?.vcm_value_high || 0;

  const vcmSignal = reduction >= 500 ? "strong" : reduction >= 100 ? "moderate" : "early";
  const SIGNAL_CONFIG = {
    strong:   { label: "Strong carbon asset potential",       color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    moderate: { label: "Moderate carbon asset potential",     color: "text-amber-700 bg-amber-50 border-amber-200"       },
    early:    { label: "Early stage — strengthen portfolio",  color: "text-slate-600 bg-slate-50 border-slate-200"       },
  };
  const sig = SIGNAL_CONFIG[vcmSignal];

  return (
    <div className="space-y-4">
      {/* Total reduction + value */}
      <div className="bg-slate-900 rounded-xl p-5 text-white">
        <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Portfolio Carbon Value (indicative)</p>
        <div className="grid grid-cols-3 gap-4">
          <MetricCard label="tCO₂e/yr" value={reduction} color="text-teal-400" />
          <MetricCard label="VCM/yr (indicative)" value={`$${(vcmLow/1000).toFixed(0)}k–$${(vcmHigh/1000).toFixed(0)}k`} color="text-white" />
          <MetricCard label="Asset potential" value={vcmSignal} color="text-emerald-400" />
        </div>
      </div>

      <div className={`rounded-xl border px-4 py-3 ${sig.color}`}>
        <p className="text-xs font-bold">{sig.label}</p>
        <p className="text-[10px] mt-0.5">
          {vcmSignal === "strong"   && "Scale and diversity of actions may support formal project development and carbon market structuring — subject to methodology fit and validation."}
          {vcmSignal === "moderate" && "Growing portfolio. Adding 1–2 high-impact actions may increase suitability for structured project development."}
          {vcmSignal === "early"    && "Portfolio is early stage. Focus on adding high-impact initiatives with clear baselines before assessing carbon market potential."}
        </p>
      </div>

      {/* Pathway signal */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs font-bold text-[#0F172A] mb-2">Pathway Guidance (indicative)</p>
        {[
          { label: "Internal decarbonization",    active: true,                 note: "All portfolios — immediate action on energy and fuel" },
          { label: "Voluntary Carbon Market",     active: reduction >= 100,     note: "Subject to additionality and methodology validation" },
          { label: "Article 6 suitability",       active: reduction >= 500,     note: "May be suitable — requires sovereign authorization and MRV protocol" },
        ].map(p => (
          <div key={p.label} className="flex items-start gap-2 mb-2">
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${p.active ? "bg-teal-500" : "bg-slate-300"}`} />
            <div>
              <p className={`text-[11px] font-semibold ${p.active ? "text-[#0F172A]" : "text-slate-400"}`}>{p.label}</p>
              <p className="text-[9px] text-slate-400">{p.note}</p>
            </div>
          </div>
        ))}
        <p className="text-[8px] text-slate-400 italic mt-2">Indicative only. DeCarbonIQ does not certify carbon market eligibility.</p>
      </div>
    </div>
  );
}

// ── STEP 6: Decision Engine ────────────────────────────────────────────────
function Step6_Decision({ summary, onGoStructure, onGoRecommend }) {
  const isStructure = summary?.recommendation === "project_structuring";
  const hasData     = (summary?.total_reduction_tco2e || 0) > 0;

  if (!hasData) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-700">Insufficient data for decision</p>
            <p className="text-xs text-amber-600 mt-0.5">Add at least one initiative with a reduction estimate in Step 4 before running the decision engine.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Based on your current portfolio ({summary.total_initiatives} initiatives, {summary.total_reduction_tco2e}t/yr potential), the Decision Engine routes you to:
      </p>

      {/* Decision output */}
      <div className={`rounded-xl border-2 p-6 ${isStructure ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
        data-testid="decision-output">
        <div className="flex items-center gap-3 mb-2">
          {isStructure
            ? <CheckCircle2 size={20} className="text-emerald-600" />
            : <AlertCircle  size={20} className="text-amber-600"   />
          }
          <p className={`text-base font-bold ${isStructure ? "text-emerald-700" : "text-amber-700"}`}>
            {isStructure ? "Ready for Project Structuring" : "Strengthen Portfolio First"}
          </p>
        </div>
        <p className={`text-sm ${isStructure ? "text-emerald-600" : "text-amber-600"} mb-4`}>
          {isStructure
            ? "Your portfolio has actionable initiatives with defined CAPEX and reduction targets. You can convert these into formal Project Assessments."
            : "Your portfolio lacks sufficient actions or defined CAPEX to support formal project structuring. Add more initiatives or review recommendations."}
        </p>
        <div className="flex items-center gap-3">
          <button onClick={isStructure ? onGoStructure : onGoRecommend}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl ${
              isStructure ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-amber-600 text-white hover:bg-amber-700"
            }`}
            data-testid="decision-cta-btn">
            <ArrowRight size={14} />
            {isStructure ? "Go to Project Structuring" : "View Recommendations"}
          </button>
        </div>
      </div>

      {/* Decision explanation — natural language, not code */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mb-2">Why this decision</p>
        {isStructure ? (
          <div className="space-y-1.5">
            {summary.total_initiatives >= 1 && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                <span>{summary.total_initiatives} initiative{summary.total_initiatives !== 1 ? "s" : ""} identified</span>
              </div>
            )}
            {summary.total_reduction_tco2e > 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                <span>{summary.total_reduction_tco2e}t CO₂e/yr emissions reduction potential</span>
              </div>
            )}
            {summary.total_capex_usd > 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                <span>CAPEX defined: ${(summary.total_capex_usd/1000).toFixed(0)}k across initiatives</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            {summary.total_initiatives === 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <AlertCircle size={12} className="text-amber-500 flex-shrink-0" />
                <span>No initiatives added — add at least one decarbonization action</span>
              </div>
            )}
            {summary.total_reduction_tco2e === 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <AlertCircle size={12} className="text-amber-500 flex-shrink-0" />
                <span>No reduction targets defined — add expected CO₂e reduction to initiatives</span>
              </div>
            )}
            {summary.total_capex_usd === 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <AlertCircle size={12} className="text-amber-500 flex-shrink-0" />
                <span>CAPEX not defined — add investment estimates to enable structuring</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── STEP 7A: Project Structuring ───────────────────────────────────────────
function Step7_Structuring({ initiatives, navigate, onCreateProject }) {
  const [busy, setBusy] = useState(null);
  const [created, setCreated] = useState([]);

  const actionable = initiatives.filter(i => i.expected_reduction_tco2e > 0 && i.capex_usd > 0);

  const handleCreate = async (initiative) => {
    setBusy(initiative.id);
    try {
      const res = await client.post("/corporate/workspace/create-project", {
        name:         `${initiative.name} — Climate Project`,
        project_type: initiative.category === "renewable_energy" ? "solar" :
                      initiative.category === "waste_reduction"  ? "waste_recovery" :
                      initiative.category === "fuel_switch"       ? "fuel_switch" :
                      initiative.category === "energy_efficiency" ? "energy_efficiency" :
                      initiative.category === "low_carbon_materials" ? "low_carbon_materials" : "other",
        stage:       "development",
        description: `Derived from corporate initiative: ${initiative.name}. Reduction target: ${initiative.expected_reduction_tco2e}t CO₂e/yr.`,
      });
      setCreated(prev => [...prev, { initiativeId: initiative.id, pia: res.data }]);
      toast.success(`Project created: ${res.data.ref_id}`);
    } catch { toast.error("Could not create project."); }
    setBusy(null);
  };

  if (!actionable.length) {
    return (
      <div className="text-center py-6">
        <ListChecks size={24} className="text-slate-200 mx-auto mb-2" />
        <p className="text-xs text-slate-400">No initiatives with both reduction targets and CAPEX defined.</p>
        <p className="text-[10px] text-slate-400 mt-1">Update your initiatives with expected_reduction_tco2e and capex_usd fields.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Convert initiatives into formal Project Assessments, or bundle multiple initiatives into a single structured climate program for larger financing.
      </p>

      {/* Program Builder CTA — primary for large portfolios */}
      {actionable.length >= 2 && (
        <div className="bg-[#0F172A] rounded-xl p-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] text-teal-400 font-bold uppercase tracking-wide mb-0.5">Recommended for {actionable.length}+ initiatives</p>
            <p className="text-sm font-bold text-white">Structure as a Climate Program</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Bundle all {actionable.length} initiatives into one program — one deal, larger ticket, greater DFI interest.
            </p>
          </div>
          <button onClick={() => navigate("/corporate/programs")}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-500"
            data-testid="go-to-program-builder-btn">
            <Layers size={13} /> Program Builder
          </button>
        </div>
      )}
      <div className="space-y-3">
        {actionable.map(i => {
          const alreadyCreated = created.find(c => c.initiativeId === i.id);
          return (
            <div key={i.id} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#0F172A]">{i.name}</p>
                <p className="text-[10px] text-slate-400">{i.expected_reduction_tco2e}t/yr · ${(i.capex_usd/1000).toFixed(0)}k CAPEX</p>
              </div>
              {alreadyCreated ? (
                <button
                  onClick={() => navigate(`/project/assessment/${alreadyCreated.pia.id}`)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700"
                  data-testid={`open-project-${i.id}`}>
                  <ArrowRight size={12} /> Open Project ({alreadyCreated.pia.ref_id})
                </button>
              ) : (
                <button
                  onClick={() => handleCreate(i)}
                  disabled={busy === i.id}
                  className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-40"
                  data-testid={`create-project-${i.id}`}>
                  {busy === i.id ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                  {busy === i.id ? "Creating..." : "Create Project Assessment"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 italic">
        Converted projects open in the Project Impact Assessment page where you can define baseline, MRV, additionality, and financial case.
      </p>
    </div>
  );
}

// ── STEP 7B: Project Recommendations ──────────────────────────────────────
function Step7B_Recommendations({ recommendations, navigate }) {
  if (!recommendations?.length) return (
    <div className="text-center py-6 text-xs text-slate-400">No recommendations available for this industry.</div>
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Industry-specific project types most relevant to your sector and geography. Add any of these to your Action Portfolio.
      </p>
      {recommendations.map((r, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-sm font-semibold text-slate-900">{r.name}</p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[9px] text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full font-bold">~{r.reduction_pct}% reduction</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${
                r.complexity === "Low" ? "bg-emerald-50 text-emerald-600" :
                r.complexity === "Medium" ? "bg-amber-50 text-amber-600" :
                "bg-slate-100 text-slate-500"
              }`}>{r.complexity}</span>
            </div>
          </div>
          <p className="text-xs text-slate-500">{r.why}</p>
          <button onClick={() => navigate("/corporate/initiatives")}
            className="mt-3 text-xs text-teal-600 font-semibold hover:underline flex items-center gap-1">
            <Plus size={11} /> Add to portfolio
          </button>
        </div>
      ))}
    </div>
  );
}

// ── STEP 8: Article 6 & NDC Alignment ─────────────────────────────────────
function Step8_NDC({ ndcData }) {
  if (!ndcData) return (
    <div className="text-center py-6 text-xs text-slate-400">
      <RefreshCw size={18} className="animate-spin mx-auto mb-2" />
    </div>
  );

  const isPriority = ndcData.is_priority_sector;

  return (
    <div className="space-y-4">
      {/* NDC overview */}
      <div className="bg-[#0F172A] rounded-xl p-5 text-white">
        <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">National Determined Contribution (NDC)</p>
        <p className="text-sm font-bold text-white">{ndcData.ndc_headline}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
            isPriority ? "bg-teal-500/20 text-teal-400" : "bg-slate-700 text-slate-400"
          }`}>
            {isPriority ? "Priority sector" : "Non-priority sector"}
          </span>
          <span className="text-[9px] text-slate-500">{ndcData.industry} · {ndcData.country}</span>
        </div>
      </div>

      {/* NDC priorities */}
      {ndcData.priority_sectors.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-bold text-[#0F172A] mb-2">NDC Priority Sectors</p>
          <div className="flex flex-wrap gap-2">
            {ndcData.priority_sectors.map(s => (
              <span key={s} className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                s === ndcData.industry ? "bg-teal-100 text-teal-700 border border-teal-200" : "bg-slate-100 text-slate-500"
              }`}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Key opportunities */}
      {ndcData.key_opportunities.length > 0 && (
        <SectionCard>

          <p className="text-xs font-bold text-[#0F172A] mb-2">NDC-Aligned Opportunities</p>
          {ndcData.key_opportunities.map(o => (
            <div key={o} className="flex items-center gap-2 text-xs text-slate-600 py-0.5">
              <span className="text-teal-500 font-bold">—</span> {o}
            </div>
          ))}
        
        </SectionCard>
      )}

      {/* Article 6 note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <Shield size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-700 mb-1">Article 6 Note</p>
            <p className="text-[10px] text-amber-600 leading-relaxed">{ndcData.article6_note}</p>
          </div>
        </div>
      </div>

      <p className="text-[9px] text-slate-400 italic">{ndcData.platform_disclaimer}</p>
    </div>
  );
}

// ── STEP 9: Documentation ──────────────────────────────────────────────────
function Step9_Documentation({ summary, navigate }) {
  const DOCS = [
    { label: "Portfolio Emissions Summary", desc: "Aggregated Scope 1/2/3 by site with data confidence", ready: summary?.total_reduction_tco2e > 0 },
    { label: "Action Plan & CAPEX Table",  desc: "All initiatives with reduction, capex, and status",    ready: summary?.total_initiatives > 0 },
    { label: "Carbon Value Assessment",    desc: "Carbon value range and pathway signal (indicative)",    ready: summary?.vcm_value_low > 0 },
    { label: "MRV Framework Outline",      desc: "Monitoring, reporting, and verification structure",      ready: summary?.total_reduction_tco2e > 0 },
  ];
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Preparation for formal project documentation, MRV structure, and registry-ready formats.</p>
      {DOCS.map(d => (
        <div key={d.label} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-xs font-bold text-[#0F172A]">{d.label}</p>
              {d.ready && <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                <CheckCircle2 size={8} /> Ready
              </span>}
            </div>
            <p className="text-[10px] text-slate-400">{d.desc}</p>
          </div>
          <button
            onClick={async () => {
              if (!d.ready) { toast.error("Add data first."); return; }
              try {
                const token = localStorage.getItem("decarboniq_token");
                const orgParam = summary?.org_id ? `?org_id=${summary.org_id}` : "";
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/export-pdf/corporate${orgParam}`;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `DCI-PortfolioSummary.pdf`;
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("Portfolio PDF downloaded.");
              } catch { toast.error("PDF generation failed."); }
            }}
            className={`flex-shrink-0 ml-3 text-xs font-bold px-3 py-1.5 rounded-xl ${
              d.ready ? "bg-teal-600 text-white hover:bg-teal-700" : "bg-slate-100 text-slate-400"
            }`}>
            {d.ready ? "Download" : "Add data"}
          </button>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 italic">Full MRV documentation and registry-ready formats are generated at the Project Assessment level.</p>
    </div>
  );
}

// ── STEP 10: Submission ────────────────────────────────────────────────────
function Step10_Submit({ summary, initiatives, navigate }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null);
  const [amount, setAmount] = useState(summary?.total_capex_usd || "");
  const [purpose, setPurpose] = useState("");

  const handleSubmit = async () => {
    if (!purpose.trim()) { toast.warning("Add a purpose for this financing request."); return; }
    setSubmitting(true);
    try {
      const res = await client.post("/finance-requests/", {
        workspace_type:      "corporate",
        request_type:        "blended_finance",
        amount_requested_usd: parseFloat(amount) || summary.total_capex_usd,
        purpose:             purpose,
        applicant_notes:     `Corporate portfolio submission: ${summary.total_initiatives} initiatives, ${summary.total_reduction_tco2e}t CO₂e/yr potential.`,
      });
      await client.post(`/finance-requests/${res.data.request_id}/submit`);
      setSubmitted(res.data.request_id);
      toast.success(`Submitted to FI queue: ${res.data.request_id}`);
    } catch { toast.error("Submission failed."); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-5">
      {/* Summary for FI */}
      <div className="bg-[#0F172A] rounded-xl p-5 text-white">
        <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-3">Corporate Portfolio — FI Submission Summary</p>
        <div className="grid grid-cols-3 gap-4">
          <div><p className="text-xl font-bold text-teal-400">{summary?.total_reduction_tco2e}t</p><p className="text-[10px] text-slate-400">CO₂e/yr</p></div>
          <div><p className="text-xl font-bold text-white">${((summary?.total_capex_usd||0)/1000).toFixed(0)}k</p><p className="text-[10px] text-slate-400">CAPEX</p></div>
          <div><p className="text-xl font-bold text-emerald-400">{summary?.total_initiatives}</p><p className="text-[10px] text-slate-400">Initiatives</p></div>
        </div>
      </div>

      {submitted ? (
        <div className="text-center py-4" data-testid="corp-submission-success">
          <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-900 mb-1">Submitted to FI Pipeline</p>
          <p className="text-xs text-slate-400 mb-4">Reference: <strong className="font-mono">{submitted}</strong></p>
          <button onClick={() => navigate("/fi/requests")}
            className="flex items-center gap-2 mx-auto px-4 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
            View in FI queue <ArrowRight size={12} />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-slate-400 font-semibold block mb-1">Total financing required (USD)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 font-mono"
                data-testid="corp-submit-amount" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 font-semibold block mb-1">Financing type</label>
              <select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none bg-white">
                <option value="blended_finance">Blended Finance</option>
                <option value="green_loan">Green Loan</option>
                <option value="capex_support">Capital Support</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 font-semibold block mb-1">Purpose / Use of funds</label>
            <textarea value={purpose} onChange={e => setPurpose(e.target.value)}
              placeholder={`Fund ${summary?.total_initiatives || ""} decarbonization initiatives across ${summary?.org_name || "the organization"}`}
              rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-400"
              data-testid="corp-submit-purpose" />
          </div>
          <button onClick={handleSubmit} disabled={submitting}
            className="w-full h-12 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="corp-submit-btn">
            {submitting ? <RefreshCw size={13} className="animate-spin" /> : <DollarSign size={13} />}
            {submitting ? "Submitting..." : "Submit Corporate Portfolio to FI Pipeline"}
          </button>
          <p className="text-[9px] text-slate-400 text-center">
            This corporate portfolio submission will appear in the FI financing queue alongside project and SME deal applications.
          </p>
        </div>
      )}
    </div>
  );
}

// ── TOP BAR ────────────────────────────────────────────────────────────────
function TopBar({ org, summary, onBack }) {
  const isReady = summary?.recommendation === "project_structuring";
  return (
    <div className="h-14 bg-[#0B1220] border-b border-white/8 flex items-center px-5 gap-4 sticky top-0 z-30"
      data-testid="corp-workspace-top-bar">
      <button onClick={onBack} className="text-slate-500 hover:text-white transition-colors text-xs">← Corporate</button>
      <div className="w-px h-4 bg-white/10" />
      <Layers size={13} className="text-teal-400" />
      <p className="text-sm font-bold text-white truncate">{org?.name || "Corporate Workspace"}</p>
      {org?.sector && <span className="text-[10px] text-slate-500">{org.sector}</span>}
      <div className="flex-1" />
      {summary && (
        <span className={`text-[10px] font-bold px-3 py-1 rounded-full border ${
          isReady ? "bg-emerald-900/20 border-emerald-600/30 text-emerald-400" : "bg-amber-900/20 border-amber-600/30 text-amber-400"
        }`}>
          {isReady ? "Ready for Structuring" : "In Progress"}
        </span>
      )}
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function CorporateWorkspace() {
  const navigate           = useNavigate();
  const { currentOrg }     = useAuth();
  const orgId              = currentOrg?.id;
  const [activeStep,    setActiveStep]    = useState("setup");
  const [summary,       setSummary]       = useState(null);
  const [initiatives,   setInitiatives]   = useState([]);
  const [recommendations,setRecs]         = useState([]);
  const [ndcData,       setNdcData]       = useState(null);
  const [readiness,     setReadiness]     = useState(null);
  const [loading,       setLoading]       = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, recRes, ndcRes, rdrRes] = await Promise.allSettled([
        client.get("/corporate/workspace/summary"),
        client.get("/corporate/workspace/recommendations"),
        client.get("/corporate/workspace/ndc-alignment"),
        client.get("/corporate/workspace/portfolio-readiness"),
      ]);
      if (sumRes.status  === "fulfilled") setSummary(sumRes.value.data);
      if (recRes.status  === "fulfilled") setRecs(recRes.value.data?.recommendations || []);
      if (ndcRes.status  === "fulfilled") setNdcData(ndcRes.value.data);
      if (rdrRes.status  === "fulfilled") setReadiness(rdrRes.value.data);

      // Use org_id from summary (backend-authoritative) to fetch initiatives
      const resolvedOrgId = sumRes.status === "fulfilled" ? sumRes.value.data?.org_id : orgId;
      if (resolvedOrgId) {
        const initRes = await client.get(`/corporate/orgs/${resolvedOrgId}/initiatives`).catch(() => ({ data: [] }));
        setInitiatives(initRes.data || []);
      }
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const completedKeys = [
    "setup",
    summary?.total_initiatives > 0 && "actions",
    summary?.total_reduction_tco2e > 0 && "hotspots",
    summary?.total_reduction_tco2e > 0 && "carbon_value",
    summary?.total_reduction_tco2e > 0 && "decision",
  ].filter(Boolean);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#0B1220]">
      <RefreshCw size={20} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="flex flex-col h-screen" data-testid="corporate-workspace">
      <TopBar org={currentOrg} summary={summary} onBack={() => navigate("/corporate")} />
      <div className="flex flex-1 min-h-0">
        <StepRail activeStep={activeStep} completedKeys={completedKeys} onStep={setActiveStep} />

        {/* Main canvas */}
        <div className="flex-1 overflow-y-auto bg-[#F8FAFC] px-6 py-6">
          <div>
            <PageHeader
              label="CORPORATE WORKSPACE"
              title={activeStep ? activeStep.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Climate Journey"}
              subtitle={`${currentOrg?.name || ""} · Step-based portfolio builder`}
              className="mb-5"
            />
            {activeStep === "setup"       && <StepCard number={1}  icon={Building2}  title="Organization Setup"          desc="Confirm your organization structure. This sets benchmark peers and NDC alignment context.">   <Step1_Setup org={currentOrg} siteCount={initiatives.length > 0 ? Math.ceil(initiatives.length / 2) : 0} summary={summary} /></StepCard>}
            {activeStep === "data"        && <StepCard number={2}  icon={BarChart2}   title="Data Aggregation"            desc="Capture Scope 1, 2, and 3 emissions across all sites and production units.">                <Step2_Data orgId={orgId} navigate={navigate} summary={summary} /></StepCard>}
            {activeStep === "hotspots"    && <StepCard number={3}  icon={Activity}    title="Impact & Hotspot Analysis"   desc="Identify where your biggest emission reduction opportunities lie.">                        <Step3_Hotspots initiatives={initiatives} summary={summary} /></StepCard>}
            {activeStep === "actions"     && <StepCard number={4}  icon={Layers}      title="Action Portfolio"            desc="Aggregate all decarbonization initiatives across the organization.">                      <Step4_Actions initiatives={initiatives} orgId={orgId} navigate={navigate} /></StepCard>}
            {activeStep === "carbon_value"&& <StepCard number={5}  icon={Zap}         title="Carbon Value & Eligibility"  desc="Translate your portfolio into carbon asset potential and pathway signals.">               <Step5_CarbonValue summary={summary} /></StepCard>}
            {activeStep === "decision"    && <StepCard number={6}  icon={GitBranch}   title="Decision Engine"            desc="Route your portfolio to the right next step based on current state.">                    <Step6_Decision summary={summary} onGoStructure={() => setActiveStep("structuring")} onGoRecommend={() => setActiveStep("structuring")} /></StepCard>}
            {activeStep === "structuring" && (
              summary?.recommendation === "project_structuring"
                ? <StepCard number={7} icon={ListChecks} title="Project Structuring" desc="Convert initiatives into formal Project Impact Assessments."><Step7_Structuring initiatives={initiatives} navigate={navigate} /></StepCard>
                : <StepCard number={7} icon={BookOpen}   title="Project Recommendations" desc="Industry-specific project types to strengthen your portfolio."><Step7B_Recommendations recommendations={recommendations} navigate={navigate} /></StepCard>
            )}
            {activeStep === "ndc"         && <StepCard number={8}  icon={Globe}       title="Article 6 & NDC Alignment"  desc="Country-level context for sovereign frameworks. All language is indicative.">             <Step8_NDC ndcData={ndcData} /></StepCard>}
            {activeStep === "documentation"&&<StepCard number={9}  icon={FileText}    title="Documentation Preparation"  desc="Prepare portfolio documentation for formal project structuring.">                        <Step9_Documentation summary={summary} navigate={navigate} /></StepCard>}
            {activeStep === "submission"  && <StepCard number={10} icon={DollarSign}  title="Submit to FI / Market"      desc="Send your structured corporate portfolio to the FI financing pipeline.">                <Step10_Submit summary={summary} initiatives={initiatives} navigate={navigate} /></StepCard>}

            {/* Mobile step nav */}
            <div className="xl:hidden mt-4 flex flex-wrap gap-1.5">
              {STEPS.map(s => (
                <button key={s.key} onClick={() => setActiveStep(s.key)}
                  className={`text-[9px] font-bold px-2 py-1 rounded-lg ${
                    activeStep === s.key ? "bg-teal-600 text-white" : "bg-white border border-slate-200 text-slate-500"
                  }`}>{s.id}. {s.label.split(" ")[0]}</button>
              ))}
            </div>
          </div>
        </div>

        <ExecutivePanel summary={summary} recommendations={recommendations} readiness={readiness} />
      </div>
    </div>
  );
}
